/**
 * Адаптер хранилища в памяти
 * Базовая реализация для тестирования и временного хранения
 */

import { EventEmitter } from 'eventemitter3';
import {
  StorageAdapter,
  StorageType,
  StorageConfig,
  StorageResult,
  StorageMetadata,
  StorageOptions,
  StorageFilter,
  StorageSearchResult,
  StorageSearchOptions,
  StorageRecord
} from '../types/persistence-interfaces';
import { SerializedElement } from '../types/interfaces';

/**
 * Адаптер хранилища в памяти
 */
export class MemoryStorageAdapter extends EventEmitter implements StorageAdapter {
  public readonly type: StorageType = StorageType.MEMORY;
  public readonly name: string;
  public readonly config: StorageConfig;

  private storage: Map<string, StorageRecord> = new Map();
  private ready: boolean = false;

  constructor(config: Partial<StorageConfig> = {}) {
    super();
    this.name = config.name || 'MemoryStorageAdapter';
    this.config = {
      type: StorageType.MEMORY,
      name: this.name,
      ...config
    };
  }

  /**
   * Инициализация адаптера
   */
  async initialize(): Promise<void> {
    this.ready = true;
    this.emit('initialized', { adapter: this.name });
  }

  /**
   * Закрытие адаптера
   */
  async close(): Promise<void> {
    this.ready = false;
    this.emit('closed', { adapter: this.name });
  }

  /**
   * Проверка готовности адаптера
   */
  isReady(): boolean {
    return this.ready;
  }

  /**
   * Сохранение элемента
   */
  async save(id: string, data: SerializedElement, options?: StorageOptions): Promise<StorageResult<StorageMetadata>> {
    if (!this.ready) {
      return {
        success: false,
        error: 'Адаптер не инициализирован'
      };
    }

    // Опции используются для совместимости с интерфейсом
    void options;

    try {
      const now = Date.now();
      const serializedData = JSON.stringify(data);
      const size = new TextEncoder().encode(serializedData).length;

      // Создаем или обновляем метаданные
      const existingRecord = this.storage.get(id);
      const version = existingRecord ? existingRecord.metadata.version + 1 : 1;

      const metadata: StorageMetadata = {
        id,
        version,
        createdAt: existingRecord?.metadata.createdAt || now,
        updatedAt: now,
        size,
        checksum: this.calculateChecksum(serializedData),
        tags: [],
        userMetadata: {}
      };

      // Создаем запись
      const record: StorageRecord = {
        id,
        data,
        metadata
      };

      // Сохраняем
      this.storage.set(id, record);

      this.emit('saved', { 
        adapter: this.name, 
        elementId: id, 
        metadata 
      });

      return {
        success: true,
        data: metadata,
        metadata
      };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Неизвестная ошибка';
      
      this.emit('save-error', { 
        adapter: this.name, 
        elementId: id, 
        error: errorMessage 
      });

      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Загрузка элемента
   */
  async load(id: string, options?: StorageOptions): Promise<StorageResult<SerializedElement>> {
    if (!this.ready) {
      return {
        success: false,
        error: 'Адаптер не инициализирован'
      };
    }

    try {
      const record = this.storage.get(id);
      
      if (!record) {
        return {
          success: false,
          error: `Элемент с ID ${id} не найден`
        };
      }

      // Проверяем контрольную сумму если требуется
      if (options?.validateChecksum && record.metadata.checksum) {
        const serializedData = JSON.stringify(record.data);
        const currentChecksum = this.calculateChecksum(serializedData);
        
        if (currentChecksum !== record.metadata.checksum) {
          return {
            success: false,
            error: 'Ошибка проверки контрольной суммы'
          };
        }
      }

      this.emit('loaded', { 
        adapter: this.name, 
        elementId: id, 
        metadata: record.metadata 
      });

      return {
        success: true,
        data: record.data,
        metadata: record.metadata
      };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Неизвестная ошибка';
      
      this.emit('load-error', { 
        adapter: this.name, 
        elementId: id, 
        error: errorMessage 
      });

      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Удаление элемента
   */
  async delete(id: string, options?: StorageOptions): Promise<StorageResult<boolean>> {
    if (!this.ready) {
      return {
        success: false,
        error: 'Адаптер не инициализирован'
      };
    }

    // Опции используются для совместимости с интерфейсом
    void options;

    try {
      const existed = this.storage.has(id);
      
      if (!existed) {
        return {
          success: false,
          error: `Элемент с ID ${id} не найден`
        };
      }

      this.storage.delete(id);

      this.emit('deleted', { 
        adapter: this.name, 
        elementId: id 
      });

      return {
        success: true,
        data: true
      };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Неизвестная ошибка';
      
      this.emit('delete-error', { 
        adapter: this.name, 
        elementId: id, 
        error: errorMessage 
      });

      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Проверка существования элемента
   */
  async exists(id: string): Promise<boolean> {
    return this.storage.has(id);
  }

  /**
   * Получение метаданных элемента
   */
  async getMetadata(id: string): Promise<StorageResult<StorageMetadata>> {
    const record = this.storage.get(id);
    
    if (!record) {
      return {
        success: false,
        error: `Элемент с ID ${id} не найден`
      };
    }

    return {
      success: true,
      data: record.metadata,
      metadata: record.metadata
    };
  }

  /**
   * Обновление метаданных элемента
   */
  async updateMetadata(id: string, metadata: Partial<StorageMetadata>): Promise<StorageResult<StorageMetadata>> {
    const record = this.storage.get(id);
    
    if (!record) {
      return {
        success: false,
        error: `Элемент с ID ${id} не найден`
      };
    }

    // Обновляем метаданные
    const updatedMetadata: StorageMetadata = {
      ...record.metadata,
      ...metadata,
      id, // ID не может быть изменен
      updatedAt: Date.now()
    };

    // Обновляем запись
    record.metadata = updatedMetadata;
    this.storage.set(id, record);

    return {
      success: true,
      data: updatedMetadata,
      metadata: updatedMetadata
    };
  }

  /**
   * Поиск элементов
   */
  async search(filter: StorageFilter, options: StorageSearchOptions = {}): Promise<StorageSearchResult> {
    const allRecords = Array.from(this.storage.values());
    
    // Применяем фильтры
    const filteredRecords = allRecords.filter(record => {
      const metadata = record.metadata;

      // Фильтр по ID
      if (filter.ids && !filter.ids.includes(metadata.id)) {
        return false;
      }

      // Фильтр по тегам
      if (filter.tags && filter.tags.length > 0) {
        const recordTags = metadata.tags || [];
        if (!filter.tags.some(tag => recordTags.includes(tag))) {
          return false;
        }
      }

      // Фильтр по дате создания
      if (filter.createdAfter && metadata.createdAt < filter.createdAfter) {
        return false;
      }
      if (filter.createdBefore && metadata.createdAt > filter.createdBefore) {
        return false;
      }

      // Фильтр по дате обновления
      if (filter.updatedAfter && metadata.updatedAt < filter.updatedAfter) {
        return false;
      }
      if (filter.updatedBefore && metadata.updatedAt > filter.updatedBefore) {
        return false;
      }

      // Фильтр по размеру
      if (filter.minSize && metadata.size < filter.minSize) {
        return false;
      }
      if (filter.maxSize && metadata.size > filter.maxSize) {
        return false;
      }

      // Фильтр по пользовательским метаданным
      if (filter.userMetadata) {
        const userMeta = metadata.userMetadata || {};
        for (const [key, value] of Object.entries(filter.userMetadata)) {
          if (userMeta[key] !== value) {
            return false;
          }
        }
      }

      return true;
    });

    // Сортировка
    if (options.sortBy) {
      const sortOrder = options.sortOrder === 'desc' ? -1 : 1;
      
      filteredRecords.sort((a, b) => {
        let aValue: any, bValue: any;
        
        switch (options.sortBy) {
          case 'createdAt':
            aValue = a.metadata.createdAt;
            bValue = b.metadata.createdAt;
            break;
          case 'updatedAt':
            aValue = a.metadata.updatedAt;
            bValue = b.metadata.updatedAt;
            break;
          case 'size':
            aValue = a.metadata.size;
            bValue = b.metadata.size;
            break;
          case 'id':
            aValue = a.metadata.id;
            bValue = b.metadata.id;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return -1 * sortOrder;
        if (aValue > bValue) return 1 * sortOrder;
        return 0;
      });
    }

    // Пагинация
    const totalCount = filteredRecords.length;
    const offset = options.offset || 0;
    const limit = options.limit || totalCount;
    
    const paginatedRecords = filteredRecords.slice(offset, offset + limit);
    const hasMore = offset + limit < totalCount;

    // Убираем данные если не требуются
    if (!options.includeData) {
      paginatedRecords.forEach(record => {
        delete (record as any).data;
      });
    }

    return {
      records: paginatedRecords,
      totalCount,
      hasMore,
      nextCursor: hasMore ? String(offset + limit) : undefined
    };
  }

  /**
   * Получение списка всех ID
   */
  async listIds(): Promise<string[]> {
    return Array.from(this.storage.keys());
  }

  /**
   * Очистка хранилища
   */
  async clear(): Promise<StorageResult<number>> {
    const count = this.storage.size;
    this.storage.clear();

    this.emit('cleared', { 
      adapter: this.name, 
      count 
    });

    return {
      success: true,
      data: count
    };
  }

  /**
   * Получение размера хранилища
   */
  async getSize(): Promise<number> {
    let totalSize = 0;
    
    for (const record of this.storage.values()) {
      totalSize += record.metadata.size;
    }

    return totalSize;
  }

  /**
   * Получение статистики хранилища
   */
  async getStats(): Promise<{
    totalRecords: number;
    totalSize: number;
    oldestRecord?: StorageMetadata;
    newestRecord?: StorageMetadata;
    averageSize: number;
  }> {
    const records = Array.from(this.storage.values());
    const totalRecords = records.length;
    
    if (totalRecords === 0) {
      return {
        totalRecords: 0,
        totalSize: 0,
        averageSize: 0
      };
    }

    let totalSize = 0;
    let oldestRecord: StorageMetadata | undefined;
    let newestRecord: StorageMetadata | undefined;

    for (const record of records) {
      totalSize += record.metadata.size;

      if (!oldestRecord || record.metadata.createdAt < oldestRecord.createdAt) {
        oldestRecord = record.metadata;
      }

      if (!newestRecord || record.metadata.createdAt > newestRecord.createdAt) {
        newestRecord = record.metadata;
      }
    }

    return {
      totalRecords,
      totalSize,
      oldestRecord,
      newestRecord,
      averageSize: totalSize / totalRecords
    };
  }

  /**
   * Вычисление простой контрольной суммы
   */
  private calculateChecksum(data: string): string {
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
      const char = data.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Преобразование в 32-битное число
    }
    return hash.toString(36);
  }
} 